import {
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  makeStyles,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@material-ui/core';

import { Organization, OrganizationType, ORGANIZATION_TYPE } from '../../types/organization.type';
import { useOrganizationEditForm } from '../../hooks/use-organization-edit-form.hook';
import { DISCOUNT_TYPES } from '../../../shared/types/discount.type';
import OrganizationLegalIdentifiersSection from './OrganizationLegalIdentifiersSection.component';

type Props = {
  organization: Organization;
  isUpdating: boolean;
  updateOrganization: (editedFields: Partial<Organization>) => Promise<void>;
};

type IdentitySectionProps = {
  type: OrganizationType;
  label: string;
  description: string;
  handleChange: ({ field, value }: { field: keyof Organization; value: string }) => void;
};

type AddressSectionProps = {
  address: Organization['address'];
  handleChange: ({ field, value }: { field: keyof Organization['address']; value: string }) => void;
};

type ContactSectionProps = {
  contact: Organization['contact'];
};

type OfferSectionProps = {
  offer: Organization['offer'];
  handleChange: ({ field, value }: { field: keyof Organization['offer']; value: string | number | boolean }) => void;
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& h4': {
      margin: 0,
      opacity: 0.7,
    },
  },

  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
  },

  body: {
    display: 'flex',
    flexDirection: 'row',
    gap: '5rem',
  },

  leftPart: {
    width: '35%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& > section': {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',

      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        padding: '1rem 0.75rem',
      },
    },
  },

  rightPart: {
    width: '65%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& > section': {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',

      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        padding: '1rem 0.75rem',
      },
    },
  },

  flexInputs: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',

    '& > div:first-child': {
      width: '65%',
    },

    '& > div:last-child': {
      width: '35%',
    },
  },

  offerRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
});

function IdentitySection({ type, label, description, handleChange }: IdentitySectionProps) {
  return (
    <section>
      <h4>Identit&eacute;</h4>
      <Paper>
        <FormControl variant="outlined">
          <InputLabel>Type de l&apos;organisation</InputLabel>
          <Select
            native
            label="Type de l'organisation"
            value={type}
            onChange={(event) => handleChange({ field: 'type', value: event.target.value as OrganizationType })}
          >
            <option value={ORGANIZATION_TYPE.CONCIERGERY}>Conciergerie</option>
            <option value={ORGANIZATION_TYPE.COMPANY}>Entreprise</option>
          </Select>
        </FormControl>
        <TextField
          label="Nom"
          type="text"
          value={label}
          variant="outlined"
          fullWidth
          onChange={(event) => handleChange({ field: 'name', value: event.target.value })}
        />
        <TextField
          label="Description"
          type="text"
          value={description}
          variant="outlined"
          multiline
          minRows={3}
          maxRows={3}
          fullWidth
          onChange={(event) => handleChange({ field: 'description', value: event.target.value })}
        />
      </Paper>
    </section>
  );
}

function AddressSection({ address, handleChange }: AddressSectionProps) {
  const classes = useStyles();

  return (
    <section>
      <h4>Adresse</h4>
      <Paper>
        <TextField
          label="Rue"
          type="text"
          value={address.street}
          variant="outlined"
          fullWidth
          onChange={(event) => handleChange({ field: 'street', value: event.target.value })}
        />
        <section className={classes.flexInputs}>
          <TextField
            label="Ville"
            type="text"
            value={address.city}
            variant="outlined"
            fullWidth
            onChange={(event) => handleChange({ field: 'city', value: event.target.value })}
          />
          <TextField
            label="Code postal"
            type="text"
            value={address.zipCode}
            variant="outlined"
            fullWidth
            onChange={(event) => handleChange({ field: 'zipCode', value: event.target.value })}
          />
        </section>
        <TextField
          label="Informations de livraison"
          type="text"
          value={address.informations}
          variant="outlined"
          multiline
          minRows={3}
          maxRows={3}
          onChange={(event) => handleChange({ field: 'informations', value: event.target.value })}
        />
      </Paper>
    </section>
  );
}

function ContactSection({ contact }: ContactSectionProps) {
  const classes = useStyles();

  return (
    <section>
      <h4>Contact</h4>
      <Paper>
        <section className={classes.flexInputs}>
          <TextField
            label="Pr&eacute;nom"
            type="text"
            value={contact.firstname ?? ''}
            variant="outlined"
            fullWidth
            disabled
          />
          <TextField
            label="Nom"
            type="text"
            value={contact.lastname ?? ''}
            variant="outlined"
            fullWidth
            disabled
          />
        </section>
        <section className={classes.flexInputs}>
          <TextField
            label="Email"
            type="email"
            value={contact.email ?? ''}
            variant="outlined"
            fullWidth
            disabled
          />
          <TextField
            label="T&eacute;l&eacute;phone"
            type="tel"
            value={contact.phoneNumber ?? ''}
            variant="outlined"
            fullWidth
            disabled
          />
        </section>
      </Paper>
    </section>
  );
}

function OfferSection({ offer, handleChange }: OfferSectionProps) {
  const classes = useStyles();

  return (
    <section>
      <h4>Offre</h4>
      <Paper>
        <section className={classes.offerRow}>
          <TextField
            label="Code"
            type="text"
            value={offer.code}
            variant="outlined"
            fullWidth
            onChange={(event) => handleChange({ field: 'code', value: event.target.value })}
          />
          <TextField
            label="Remise"
            type="number"
            value={offer.discount}
            variant="outlined"
            fullWidth
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {offer.reduction === DISCOUNT_TYPES.PERCENT ? '%' : '\u20AC'}
                </InputAdornment>
              ),
            }}
            onChange={(event) => handleChange({ field: 'discount', value: Number(event.target.value) })}
          />
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Type de remise</InputLabel>
            <Select
              native
              label="Type de remise"
              value={offer.reduction}
              onChange={(event) => handleChange({ field: 'reduction', value: event.target.value as string })}
            >
              <option value={DISCOUNT_TYPES.PERCENT}>Pourcentage</option>
              <option value={DISCOUNT_TYPES.FLAT}>Montant fixe</option>
            </Select>
          </FormControl>
        </section>
        <FormControlLabel
          control={
            <Checkbox
              checked={offer.isRestricted}
              onChange={(event) => handleChange({ field: 'isRestricted', value: event.target.checked })}
              color="primary"
            />
          }
          label="Offre restreinte aux membres"
        />
      </Paper>
    </section>
  );
}

export default function OrganizationInformationsTab({ organization, isUpdating, updateOrganization }: Props) {
  const classes = useStyles();
  const {
    isEditing,
    updatedOrganization,
    handleIdentityChange,
    handleAddressChange,
    handleOfferChange,
    handleCancelEditing,
    handleSave,
  } = useOrganizationEditForm(organization);

  return (
    <section className={classes.root}>
      <div className={classes.header}>
        <Button size="small" variant="text" onClick={handleCancelEditing} disabled={!isEditing || isUpdating}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={() => handleSave(updateOrganization)}
          disabled={!isEditing || isUpdating}
          startIcon={isUpdating ? <CircularProgress size={16} /> : null}
        >
          {isUpdating ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
      <section className={classes.body}>
        <div className={classes.leftPart}>
          <IdentitySection
            type={updatedOrganization.type}
            label={updatedOrganization.name}
            description={updatedOrganization.description ?? ''}
            handleChange={handleIdentityChange}
          />
          <AddressSection address={updatedOrganization.address} handleChange={handleAddressChange} />
        </div>
        <div className={classes.rightPart}>
          <ContactSection contact={updatedOrganization.contact} />
          <OrganizationLegalIdentifiersSection
            siren={updatedOrganization.siren ?? ''}
            vatNumber={updatedOrganization.vatNumber ?? ''}
            handleChange={handleIdentityChange}
          />
          <OfferSection offer={updatedOrganization.offer} handleChange={handleOfferChange} />
        </div>
      </section>
    </section>
  );
}
